import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, GraduationCap, Loader2, Sparkles, Trophy, AlertCircle, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { API } from "../../lib/api";
import { RewardModal } from "./RewardModal";

const T = {
  es: {
    pro_only: "Activa PRO para ganar R₿L mientras aprendes",
    reading: "Tiempo de lectura",
    reward: "Recompensa al completar",
    burned: "se quemará · ayudas al token",
    waiting_quiz: "Aprueba el quiz para reclamar",
    timer_progress: "Tiempo de lectura",
    ready: "Capítulo listo para reclamar",
    claim: "Reclamar R₿L",
    claiming: "Reclamando…",
    already: "Capítulo ya reclamado",
    daily_full: "Límite diario alcanzado",
    cooldown: "Próximo capítulo en",
    starting: "Preparando recompensa…",
    inactive: "Mueve el cursor para sumar tiempo",
  },
  en: {
    pro_only: "Activate PRO to earn R₿L while learning",
    reading: "Reading time",
    reward: "Reward on completion",
    burned: "will be burned · helping the token",
    waiting_quiz: "Pass the quiz to claim",
    timer_progress: "Reading time",
    ready: "Chapter ready to claim",
    claim: "Claim R₿L",
    claiming: "Claiming…",
    already: "Already claimed",
    daily_full: "Daily limit reached",
    cooldown: "Next chapter in",
    starting: "Preparing reward…",
    inactive: "Move the cursor to keep counting",
  },
  sk: {
    pro_only: "Aktivuj PRO a zarábaj R₿L pri učení",
    reading: "Čas čítania",
    reward: "Odmena po dokončení",
    burned: "sa spáli · pomáhaš tokenu",
    waiting_quiz: "Aprob kvíz pre claim",
    timer_progress: "Čas čítania",
    ready: "Kapitola pripravená na claim",
    claim: "Reklamuj R₿L",
    claiming: "Reklamujem…",
    already: "Už uplatnené",
    daily_full: "Denný limit dosiahnutý",
    cooldown: "Ďalšia kapitola o",
    starting: "Pripravujem odmenu…",
    inactive: "Pohni kurzorom aby čas pokračoval",
  }, de: { pro_only: "Aktiviere PRO, um R₿L beim Lernen zu verdienen", reading: "Lesezeit", reward: "Belohnung bei Abschluss", burned: "wird verbrannt · hilft dem Token", waiting_quiz: "Bestehe das Quiz, um zu beanspruchen", timer_progress: "Lesezeit", ready: "Kapitel bereit zum Beanspruchen", claim: "R₿L beanspruchen", claiming: "Wird beansprucht…", already: "Kapitel bereits beansprucht", daily_full: "Tageslimit erreicht", cooldown: "Nächstes Kapitel in", starting: "Belohnung wird vorbereitet…", inactive: "Bewege den Cursor, um Zeit hinzuzufügen" }, "zh-CN": { pro_only: "激活 PRO 以在学习时赚取 R₿L", reading: "阅读时间", reward: "完成后的奖励", burned: "将被销毁 · 帮助代币", waiting_quiz: "通过测验以领取", timer_progress: "阅读时间", ready: "章节可领取", claim: "领取 R₿L", claiming: "领取中…", already: "章节已领取", daily_full: "已达每日上限", cooldown: "下一章节", starting: "准备奖励中…", inactive: "移动光标以累积时间" },
};

const fmt = (n) => Number(n || 0).toLocaleString("es-ES");

const formatTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const ChapterRewardBar = ({ lessonKey, wordCount = 1800, level = 0 }) => {
  const { user, token, progress, refreshUser } = useAuth();
  const { lang } = useLanguage();
  const t = T[lang] || T.en || T.es;

  const isPro = !!user?.is_pro;
  const quizPassed = !!progress?.completed_keys?.includes(lessonKey);

  const [session, setSession] = useState(null);
  const [activeTime, setActiveTime] = useState(0);
  const [minTime, setMinTime] = useState(0);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(null); // result payload for modal
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const isMountedRef = useRef(true);

  const authedFetch = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(opts.headers || {}),
      },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const e = new Error(body?.detail?.code || body?.detail || res.statusText);
      e.detail = body?.detail;
      e.status = res.status;
      throw e;
    }
    return body;
  }, [token]);

  // Activity tracking — bump timestamp on any user interaction
  useEffect(() => {
    if (!isPro) return undefined;
    const bump = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener("mousemove", bump, { passive: true });
    window.addEventListener("scroll", bump, { passive: true });
    window.addEventListener("keydown", bump, { passive: true });
    window.addEventListener("touchstart", bump, { passive: true });
    return () => {
      window.removeEventListener("mousemove", bump);
      window.removeEventListener("scroll", bump);
      window.removeEventListener("keydown", bump);
      window.removeEventListener("touchstart", bump);
    };
  }, [isPro]);

  // Start a session whenever the lesson changes (only if PRO + not already L2E-claimed)
  useEffect(() => {
    isMountedRef.current = true;
    if (!isPro || !token || !lessonKey) return undefined;
    setSession(null);
    setActiveTime(0);
    setMinTime(0);
    setError(null);
    setClaimed(null);
    setAlreadyClaimed(false);

    (async () => {
      try {
        const data = await authedFetch("/learn/session/start", {
          method: "POST",
          body: JSON.stringify({ lesson_key: lessonKey, word_count: wordCount }),
        });
        if (!isMountedRef.current) return;
        setSession(data);
        setMinTime(data.min_time);
      } catch (e) {
        if (!isMountedRef.current) return;
        // Already-completed chapter or cooldown — soft fail, hide UI
        if (e.status === 403 && /Previous chapter/i.test(String(e.detail))) {
          setError({ code: "previous_required", detail: e.detail });
        } else if (e.status === 429) {
          setError({ code: typeof e.detail === "object" ? e.detail?.code : "limit", detail: e.detail });
        } else if (e.status === 403 && /PRO/i.test(String(e.detail))) {
          setError({ code: "no_pro" });
        } else {
          setError({ code: "start_failed", detail: String(e.detail || e.message) });
        }
      }
    })();

    return () => { isMountedRef.current = false; };
  }, [lessonKey, wordCount, isPro, token, authedFetch]);

  // Ping every 30s while tab active + recent activity
  useEffect(() => {
    if (!session?.session_id) return undefined;
    let stopped = false;
    const interval = setInterval(async () => {
      if (stopped) return;
      const isActive = document.hasFocus() && (Date.now() - lastActivityRef.current) < 30_000;
      if (!isActive) return;
      try {
        const data = await authedFetch("/learn/ping", {
          method: "POST",
          body: JSON.stringify({ session_id: session.session_id }),
        });
        if (!stopped) setActiveTime(data.active_time);
      } catch { /* swallow */ }
    }, 30_000);
    return () => { stopped = true; clearInterval(interval); };
  }, [session?.session_id, authedFetch]);

  const handleClaim = async () => {
    if (!session?.session_id || claiming) return;
    setClaiming(true);
    setError(null);
    try {
      // Trust the legacy quiz pass (progress.completed_keys) → quiz_passed=true, score=3/3
      const data = await authedFetch("/learn/complete", {
        method: "POST",
        body: JSON.stringify({
          session_id: session.session_id,
          quiz_passed: true,
          quiz_score: 3,
          quiz_total: 3,
        }),
      });
      setClaimed(data);
      setAlreadyClaimed(true);
      try { await refreshUser?.(); } catch { /* noop */ }
    } catch (e) {
      const code = typeof e.detail === "object" ? e.detail?.code : e.detail;
      setError({ code: code || "claim_failed", detail: e.detail });
    } finally {
      setClaiming(false);
    }
  };

  // ── Render guards ──────────────────────────────────────────────
  if (!isPro) {
    return (
      <div
        className="mt-6 rounded-xl border border-orange-400/30 bg-orange-500/[0.06] px-4 py-3 backdrop-blur"
        data-testid="rbl-bar-not-pro"
      >
        <div className="flex items-center gap-2 text-xs text-orange-200">
          <GraduationCap className="h-4 w-4" /> {t.pro_only}
        </div>
      </div>
    );
  }

  if (error?.code === "previous_required" || error?.code === "no_pro") return null;

  const reward = session?.reward_amount ?? 0;
  const burn = session?.burn_amount ?? Math.floor(reward * 0.05);
  const pct = minTime ? Math.min(100, Math.round((activeTime / minTime) * 100)) : 0;
  const timerReady = activeTime >= minTime && minTime > 0;
  const canClaim = timerReady && quizPassed && !alreadyClaimed && !claiming;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6 overflow-hidden rounded-2xl border border-[#F5D76E]/30 bg-[#F5D76E]/[0.04] p-5 backdrop-blur-md"
        data-testid="chapter-reward-bar"
      >
        <div className="flex flex-wrap items-center gap-2">
          <GraduationCap className="h-4 w-4 text-[#F5D76E]" />
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F5D76E]/90">
            LEARN-TO-EARN
          </span>
          <span className="ml-auto font-mono text-[11px] text-white/60">{lessonKey}</span>
        </div>

        {!session ? (
          <div className="mt-3 flex items-center gap-2 text-xs text-white/60">
            {error?.code === "cooldown" ? (
              <>
                <Loader2 className="h-3.5 w-3.5" />
                {t.cooldown} {Math.ceil((error?.detail?.wait_seconds || 0) / 60)}min
              </>
            ) : error?.code === "daily_chapters_limit" || error?.code === "daily_rbl_limit" ? (
              <>
                <AlertCircle className="h-3.5 w-3.5 text-orange-300" /> {t.daily_full}
              </>
            ) : (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.starting}
              </>
            )}
          </div>
        ) : (
          <>
            <div className="mt-3 flex flex-wrap items-baseline gap-2">
              <span className="text-xs text-white/70">{t.timer_progress}</span>
              <span className="font-mono text-sm font-bold text-white" data-testid="rbl-timer-display">
                {formatTime(activeTime)} / {formatTime(minTime)}
              </span>
              <span className="ml-auto font-mono text-xs text-[#F5D76E]">
                {pct}%
              </span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/5">
              <motion.div
                className="h-full bg-gradient-to-r from-orange-500 to-amber-400"
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                data-testid="rbl-timer-fill"
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <Trophy className="h-3.5 w-3.5 text-[#F5D76E]" />
                <span className="text-white/70">{t.reward}:</span>
                <span className="font-bold text-white" data-testid="rbl-reward-amount">
                  {fmt(reward)} R₿L
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-white/55">
                <Flame className="h-3.5 w-3.5 text-orange-400" />
                <span>{fmt(burn)} R₿L {t.burned}</span>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <AnimatePresence mode="wait">
                {alreadyClaimed ? (
                  <motion.div
                    key="done"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="flex items-center gap-2 rounded-full bg-emerald-500/15 px-4 py-2 text-xs font-bold text-emerald-300"
                    data-testid="rbl-bar-claimed"
                  >
                    <CheckCircle2 className="h-4 w-4" /> {t.already}
                  </motion.div>
                ) : canClaim ? (
                  <motion.button
                    key="claim"
                    initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                    onClick={handleClaim}
                    disabled={claiming}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-5 py-2 text-xs font-black uppercase tracking-[0.2em] text-black shadow-[0_0_24px_rgba(255,140,0,0.45)] transition hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.65)] disabled:opacity-50"
                    data-testid="rbl-claim-btn"
                  >
                    {claiming ? (
                      <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.claiming}</>
                    ) : (
                      <><Sparkles className="h-3.5 w-3.5" /> {t.claim} · +{fmt(reward - burn)} R₿L</>
                    )}
                  </motion.button>
                ) : timerReady && !quizPassed ? (
                  <motion.div
                    key="waiting-quiz"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="text-xs text-orange-200/80"
                    data-testid="rbl-waiting-quiz"
                  >
                    {t.ready} · {t.waiting_quiz} ↓
                  </motion.div>
                ) : (
                  <motion.div
                    key="reading"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="flex items-center gap-1.5 text-xs text-white/55"
                  >
                    <Loader2 className="h-3 w-3 animate-pulse" /> {t.inactive}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {error?.code && error.code !== "cooldown" && error.code !== "daily_chapters_limit" && (
              <p className="mt-3 text-[11px] text-red-300/80" data-testid="rbl-bar-error">
                {String(error.detail || error.code)}
              </p>
            )}
          </>
        )}
      </motion.div>

      <RewardModal
        open={!!claimed}
        onClose={() => setClaimed(null)}
        result={claimed}
        lessonKey={lessonKey}
      />
    </>
  );
};

export default ChapterRewardBar;
